import React, { useEffect, useState } from 'react';
import { Clock, TrendingUp, Award, ArrowRight, Flame } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formations as mockFormations } from '../../mock/mockData';
import { fetchPopularFormations } from '../../lib/api';
import { cleanFormationTitle, isProgrammeTag } from '../../lib/formationDisplay';
import { Button } from '../ui/button';

const typeColors = {
  'E-Learning': 'bg-sky-50 text-sky-700 border-sky-200',
  Présentiel: 'bg-amber-50 text-amber-700 border-amber-200',
  Mixte: 'bg-purple-50 text-purple-700 border-purple-200',
  Hybride: 'bg-violet-50 text-violet-800 border-violet-200',
  'Classe Virtuelle': 'bg-cyan-50 text-cyan-800 border-cyan-200',
  'Évaluation Pratique': 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const fmtEuro = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(Number(n));
};

const isDev = process.env.NODE_ENV === 'development';

const PopularFormations = () => {
  const [popular, setPopular] = useState(() =>
    isDev ? mockFormations.filter((f) => f.popular).slice(0, 8) : [],
  );
  useEffect(() => {
    fetchPopularFormations()
      .then((data) => {
        if (data?.length) setPopular(data.slice(0, 8));
        else if (isDev) setPopular(mockFormations.filter((f) => f.popular).slice(0, 8));
      })
      .catch(() => {
        if (isDev) setPopular(mockFormations.filter((f) => f.popular).slice(0, 8));
      });
  }, []);

  return (
    <section className="py-24 bg-gradient-to-b from-slate-50/50 to-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-5 mb-12">
          <div>
            <span className="text-teal-600 text-sm font-bold uppercase tracking-widest flex items-center gap-2">
              <Flame className="w-4 h-4" /> Tendances
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mt-3 tracking-tight">Nos formations les plus populaires</h2>
            <p className="text-slate-600 mt-3 max-w-xl">Sélection des modules les plus demandés par nos professionnels de santé.</p>
          </div>
          <Link to="/formations">
            <Button variant="outline" className="border-slate-300 hover:bg-slate-100">Voir tout le catalogue <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {popular.map((f) => (
            <div key={f.id} className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:border-teal-400 hover:shadow-xl hover:shadow-teal-500/10 hover:-translate-y-1 transition-all duration-300 flex flex-col">
              <div className="h-24 bg-gradient-to-br from-teal-600 via-emerald-600 to-teal-700 relative overflow-hidden">
                <div className="absolute inset-0 opacity-25" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                <div className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 rounded-full border border-white/30 bg-white/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white backdrop-blur-sm">
                  <TrendingUp className="w-3 h-3" /> Tendance
                </div>
                <div className="absolute top-2.5 right-2.5 inline-flex items-center gap-1 rounded-md border border-white/25 bg-white/10 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
                  <Award className="w-3 h-3" /> DPC
                </div>
              </div>

              <div className="p-5 flex-1 flex flex-col min-h-0">
                <div className="flex flex-wrap items-start gap-2 mb-3">
                  <span
                    className={`inline-flex max-w-full items-center rounded-lg border px-2.5 py-1 text-[11px] font-semibold leading-tight ${
                      typeColors[f.type] || 'bg-slate-50 text-slate-700 border-slate-200'
                    }`}
                  >
                    {f.type}
                  </span>
                </div>
                <h3 className="text-sm font-bold text-slate-900 leading-snug mb-3 line-clamp-3 min-h-[52px] group-hover:text-teal-700 transition-colors break-words">
                  {cleanFormationTitle(f.title)}
                </h3>
                <div className="flex flex-col gap-2 text-xs text-slate-500 mb-4">
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5 shrink-0" />
                    <span className="font-medium text-slate-700">{f.hours} h</span>
                  </div>
                  {f.tag && (
                    <div>
                      {isProgrammeTag(f.tag) ? (
                        <span className="inline-flex max-w-full rounded-md border border-teal-200 bg-teal-50 px-2 py-1 text-[11px] font-medium text-teal-900">
                          {f.tag}
                        </span>
                      ) : (
                        <span className="inline-flex max-w-full rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-medium text-slate-700">
                          {f.tag}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="mt-auto pt-4 border-t border-slate-100 flex items-end justify-between gap-2">
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide">Prix</p>
                    <p className="text-xl font-bold text-slate-900 tabular-nums">{fmtEuro(f.price)}&nbsp;€</p>
                    {f.indemnite > 0 && (
                      <p className="text-[10px] text-emerald-600 font-semibold">+&nbsp;{fmtEuro(f.indemnite)}&nbsp;€ indemnité</p>
                    )}
                  </div>
                  <Button size="sm" className="bg-slate-900 hover:bg-teal-600 text-white text-xs h-8 shrink-0" asChild>
                    <Link to={`/formations/${f.id}`}>Détails</Link>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PopularFormations;
